import { runInAction } from "mobx";
import { useEffect, useMemo, useRef } from "react";
import { EProductPlanPriceCurrency } from "@devowl-wp/api-real-commerce";
import { RealCommerceError } from "../../../exceptions/realCommerceError.js";
import { buildMatomoEventNameWithProductMeta, trackAnalyticsEvent } from "../../../others/tracking.js";
import { useStores } from "../../../store/stores.js";
import { COUPON_FORM_VALUE_KEY } from "../components/right/CouponInput.js";
const BOOTSTRAP_TIMEOUT_MS = 30000;
/**
 * Bootstraps the checkout transaction from props plus URL query params
 * (`recover`, `pay`, `cashback-token`, `coupon`, `product`, `plan`,
 * `currency`) — query params take precedence. A `cashback-token` skips the
 * product bootstrap so the claim flow can run on its own. Errors and hard
 * timeouts land in `store.fatalError` for inline error rendering.
 */ function useCheckoutV2Transaction({ product, plan, currency, coupon, customercenter }) {
    const { checkoutV2Store } = useStores();
    const { loading } = checkoutV2Store;
    // Matomo bootstrap-event guards: each event must fire at most once per
    // mount, even if MobX re-renders cycle through the watched product
    // multiple times. Stays in refs so the values survive re-renders without
    // re-triggering the effect below.
    const hasTrackedAbandonedCheckoutRecoveryRef = useRef(false);
    const hasTrackedPayLinkOpenedRef = useRef(false);
    // Stash the customer-center permalink onto the store so post-submit UI
    // (cancellation line, success view) can link to it without prop drilling.
    useEffect(()=>{
        checkoutV2Store.setCustomerCenterUrl(customercenter);
    }, [
        checkoutV2Store,
        customercenter
    ]);
    // Allow to override shortcode attributes from query parameters
    const params = useMemo(()=>new URLSearchParams(window.location.search), []);
    const useRecoverUuid = params.get("recover");
    const usePayLinkUuid = params.get("pay");
    const useProductId = params.get("product") || product?.toString() || "0";
    const useProductPlanId = params.get("plan") || plan?.toString() || "0";
    const useCurrency = params.get("currency") || currency || EProductPlanPriceCurrency.EUR;
    const useCoupon = params.get("coupon") || coupon || undefined;
    const cashbackToken = params.get("cashback-token");
    // Seed the coupon onto the store so the identification form + summary components
    // observe it on their *first* render — a `useEffect` would run one tick
    // later and cause the Order Summary to flash "Got a coupon?" before the
    // restored coupon shows up. The form will forward it to Paddle on submit.
    if (useCoupon && checkoutV2Store.formValues[COUPON_FORM_VALUE_KEY] !== useCoupon) {
        runInAction(()=>{
            checkoutV2Store.formValues[COUPON_FORM_VALUE_KEY] = useCoupon;
        });
    }
    // Load product initially for this checkout process
    useEffect(()=>{
        // When there is a cashback token, the standard product-plan bootstrap is skipped —
        // the claim UI handles the request itself.
        if (cashbackToken) {
            return undefined;
        }
        // Clear any previous fatal-error state before re-running bootstrap
        checkoutV2Store.setFatalError(undefined);
        // Hard client-side timeout so an unresponsive backend does not leave the
        // user stuck on the loading skeleton forever.
        const timeoutHandle = window.setTimeout(()=>{
            if (!checkoutV2Store.product && !checkoutV2Store.fatalError) {
                checkoutV2Store.setFatalError(new RealCommerceError({
                    status: undefined,
                    responseJSON: [
                        {
                            code: "CheckoutBootstrapTimeout",
                            message: "Bootstrap timed out."
                        }
                    ]
                }));
            }
        }, BOOTSTRAP_TIMEOUT_MS);
        const task = useRecoverUuid ? checkoutV2Store.recoverIncompleteCheckout(useRecoverUuid) : checkoutV2Store.startTransaction(usePayLinkUuid ? {
            payLinkUuid: usePayLinkUuid
        } : {
            productId: useProductId,
            productPlanId: useProductPlanId,
            currency: useCurrency
        });
        task.catch((e)=>{
            // Every error during bootstrap is terminal — there is no partial-state recovery.
            // The FatalError view inspects the error code to render the matching explanation.
            if (e instanceof RealCommerceError) {
                checkoutV2Store.setFatalError(e);
            }
        }).finally(()=>{
            window.clearTimeout(timeoutHandle);
        });
        return ()=>{
            window.clearTimeout(timeoutHandle);
        };
    }, [
        checkoutV2Store,
        cashbackToken,
        useRecoverUuid,
        usePayLinkUuid,
        useProductId,
        useProductPlanId,
        useCurrency
    ]);
    // Matomo bootstrap events — fired once per mount when the product has
    // been resolved and the bootstrap mode (recover / pay-link) is known.
    // `recoverIncompleteCheckout` doesn't await `startTransaction`, so the
    // bootstrap promise can resolve before the product is loaded. Watching
    // the product observable instead is reactive and lines up with the
    // store's MobX flow. The `recover` / `pay` query-parameter values are
    // personal data and must not be sent to analytics — we only forward
    // the resolved product meta.
    const { product: currentProduct } = checkoutV2Store;
    useEffect(()=>{
        if (!currentProduct) {
            return;
        }
        const { productPlan: { id: productPlanId, product: { id: productId, name: productName } } } = currentProduct.data;
        const productTrackingMeta = {
            product_id: String(productId),
            product_name: productName,
            product_plan_id: String(productPlanId)
        };
        if (useRecoverUuid && !hasTrackedAbandonedCheckoutRecoveryRef.current) {
            trackAnalyticsEvent("abandoned_checkout_recovery", {
                ga: {
                    event_category: "ecommerce",
                    event_label: "Abandoned checkout recovery",
                    ...productTrackingMeta
                },
                matomo: {
                    category: "ecommerce",
                    name: buildMatomoEventNameWithProductMeta("Abandoned checkout recovery", productTrackingMeta)
                }
            });
            hasTrackedAbandonedCheckoutRecoveryRef.current = true;
        }
        if (usePayLinkUuid && !hasTrackedPayLinkOpenedRef.current) {
            trackAnalyticsEvent("pay_link_opened", {
                ga: {
                    event_category: "ecommerce",
                    event_label: "Pay link opened",
                    ...productTrackingMeta
                },
                matomo: {
                    category: "ecommerce",
                    name: buildMatomoEventNameWithProductMeta("Pay link opened", productTrackingMeta)
                }
            });
            hasTrackedPayLinkOpenedRef.current = true;
        }
    }, [
        currentProduct,
        useRecoverUuid,
        usePayLinkUuid
    ]);
    return {
        loading,
        store: checkoutV2Store,
        coupon: useCoupon,
        cashbackToken
    };
}
export { useCheckoutV2Transaction };
